import axios from "axios";
import {
  dataLocalStorage,
  getLocalStorage,
  saveLocalStorage,
  removeAllLocalStorage,
} from "src/helper/publicFunction";
import { handleAxiosError } from "./errorHandler";

// Create a dedicated axios instance with credentials
const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "http://localhost:8080",
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Also configure default axios as fallback
axios.defaults.withCredentials = true;
axios.defaults.baseURL = `${
  import.meta.env.VITE_API_URL || "http://localhost:8080"
}`;

// Flag to prevent multiple refresh calls
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axiosInstance.interceptors.request.use(
  async (config) => {
    const userData = getLocalStorage(dataLocalStorage.userinfo);
    const accessToken = userData?.token;

    // Don't attach token for refresh requests to avoid 401 loops if token is expired
    const isRefreshRequest = config.url?.includes("/api/v1/auth/refreshToken");

    if (accessToken && !isRefreshRequest) {
      config.headers = {
        ...config.headers,
        token: accessToken,
      };
      console.log("Request interceptor - Token added:", {
        token: accessToken.substring(0, 20) + "...",
        url: config.url,
        timestamp: new Date().toISOString(),
      });
    } else if (isRefreshRequest) {
      console.log("Request interceptor - Skipping token for refresh request");
    } else {
      console.log("Request interceptor - No token available", {
        url: config.url,
        hasUserData: !!userData,
        hasToken: !!accessToken,
      });
    }

    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => {
    try {
      const data = response?.data || {};
      const privileges =
        data?.results?.dataPrivileges ||
        data?.dataPrivileges ||
        data?.data?.dataPrivileges ||
        data?.privileges;
      if (privileges) {
        saveLocalStorage(dataLocalStorage.privileges, privileges);
      }

      const user =
        data?.results?.admin ||
        data?.data?.user ||
        data?.user ||
        data?.results?.user ||
        data?.data;
      if (user && user?.token) {
        saveLocalStorage(dataLocalStorage.userinfo, user);
      }
    } catch (e) {
      // ignore
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const requestUrl = originalRequest?.url || "";

    if (status === 401) {
      console.log("401 error detected for URL:", requestUrl);
      console.log("401 error response:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
      });
    }

    const isLoginRequest = requestUrl.includes("/api/v1/auth/logIn");
    const isRefreshRequest = requestUrl.includes("/api/v1/auth/refreshToken");

    // If the refresh token request itself returns 401, force logout + redirect.
    if (status === 401 && isRefreshRequest) {
      console.error(
        "Protected refresh token request returned 401 – forcing logout.",
      );
      try {
        await axiosInstance.post("/api/v1/auth/logout");
      } catch (logoutError) {
        // ignore logout errors
      }

      removeAllLocalStorage();
      window.location.href = "/auth/login";

      return Promise.reject(error);
    }

    if (status === 401 && !originalRequest._retry) {
      // Don't attempt refresh if the request is to the login endpoint
      if (isLoginRequest) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            console.log("Queue processing - Using token from queue:", {
              token: token.substring(0, 20) + "...",
              url: originalRequest.url,
            });
            originalRequest.headers["token"] = token;
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try to get refresh token from localStorage first, then use cookies
        const refreshToken = localStorage.getItem("refreshToken");

        let response;
        if (refreshToken) {
          // If we have refresh token in localStorage, send it in the request body
          response = await axiosInstance.post("/api/v1/auth/refreshToken", {
            refreshToken: refreshToken,
          });
        } else {
          // Otherwise, rely on cookies (HTTP-only cookie)
          response = await axiosInstance.post("/api/v1/auth/refreshToken");
        }

        // Normalise payload so it works for both:
        // 1) { token, user, ... }
        // 2) { data: { token, user, ... } }
        const rawData = response?.data || {};
        const payload =
          rawData?.data &&
          (rawData.data.token ||
            rawData.data.accessToken ||
            rawData.data.user ||
            rawData.data.admin)
            ? rawData.data
            : rawData;

        const newUser = payload;

        // Support both `token` and `accessToken` from backend
        const newToken = payload?.token || payload?.accessToken;

        if (newToken) {
          // Get the old token for comparison
          const oldToken = getLocalStorage(dataLocalStorage.userinfo)?.token;

          // Decode JWT to check expiration
          try {
            const tokenPayload = JSON.parse(atob(newToken.split(".")[1]));
            const expirationTime = new Date(tokenPayload.exp * 1000);
            const currentTime = new Date();
            console.log("Token details:", {
              issuedAt: new Date(tokenPayload.iat * 1000),
              expiresAt: expirationTime,
              currentTime: currentTime,
              timeUntilExpiry: expirationTime - currentTime,
              isExpired: expirationTime <= currentTime,
              uuid: tokenPayload.uuid,
              email: tokenPayload.email,
              iat: tokenPayload.iat,
              exp: tokenPayload.exp,
              oldToken: oldToken ? oldToken.substring(0, 20) + "..." : "NONE",
              newToken: newToken.substring(0, 20) + "...",
              tokensMatch: oldToken === newToken,
            });
          } catch (e) {
            console.log("Could not decode token:", e.message);
          }
          // Get existing user info to preserve firstName, lastName, email, etc.
          const existingUserInfo =
            getLocalStorage(dataLocalStorage.userinfo) || {};

          // Check if response includes user data in different structures
          const userFromResponse =
            payload?.user || payload?.admin || rawData?.user || rawData?.admin;

          // Merge: existing user info (preserves firstName, lastName) + response user data
          // Token is set LAST to ensure it's not overwritten
          const updatedUser = {
            ...existingUserInfo,
            ...(userFromResponse || {}), // Use user data from response if available
            ...newUser, // Include all data from newUser (flatten)
            // Preserve critical fields if they exist in existing user info
            firstName:
              userFromResponse?.firstName ||
              newUser.firstName ||
              existingUserInfo.firstName,
            lastName:
              userFromResponse?.lastName ||
              newUser.lastName ||
              existingUserInfo.lastName,
            email:
              userFromResponse?.email ||
              newUser.email ||
              existingUserInfo.email,
            uuid:
              userFromResponse?.uuid || newUser?.uuid || existingUserInfo.uuid,
            // Set token LAST to ensure it's always the refreshed token
            token: newToken,
          };

          // Avoid storing nested user/admin objects inside the user object
          if (updatedUser.user) {
            delete updatedUser.user;
          }
          if (updatedUser.admin) {
            delete updatedUser.admin;
          }

          console.log("Saving refreshed token to localStorage:", {
            token: newToken.substring(0, 20) + "...",
            fullToken: newToken,
          });
          saveLocalStorage(dataLocalStorage.userinfo, updatedUser);

          // Verify the token was saved correctly
          const savedUser = getLocalStorage(dataLocalStorage.userinfo);
          console.log("Verification - Token in localStorage after save:", {
            savedToken: savedUser?.token
              ? savedUser.token.substring(0, 20) + "..."
              : "NO TOKEN",
            matchesNewToken: savedUser?.token === newToken,
          });

          // Save new refresh token if provided (support multiple shapes)
          const newRefreshToken =
            payload?.refreshToken ||
            rawData?.refreshToken ||
            rawData?.data?.refreshToken;
          if (newRefreshToken) {
            console.log("Saving new refresh token");
            localStorage.setItem("refreshToken", newRefreshToken);
          }

          // Update axios default headers with new token
          axiosInstance.defaults.headers.common["token"] = newToken;
          originalRequest.headers["token"] = newToken;

          // Also try with Authorization header as fallback
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;

          console.log(
            "Token refresh successful, retrying original request with new token:",
            {
              newToken: newToken.substring(0, 20) + "...",
              originalUrl: originalRequest.url,
              headerToken:
                originalRequest.headers.token.substring(0, 20) + "...",
              authorizationHeader: originalRequest.headers.Authorization,
              allHeaders: originalRequest.headers,
            },
          );
          processQueue(null, newToken);

          // Small delay to ensure localStorage is updated before retrying
          await new Promise((resolve) => setTimeout(resolve, 10));

          // Create a new request config to ensure fresh token is used
          const retryConfig = {
            ...originalRequest,
            headers: {
              ...originalRequest.headers,
              token: newToken,
              Authorization: `Bearer ${newToken}`,
            },
          };

          return axiosInstance(retryConfig);
        } else {
          console.log("No token in refresh response");
          throw new Error("No token in refresh response");
        }
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        processQueue(refreshError, null);
        try {
          await axiosInstance.post("/api/v1/auth/logout");
        } catch (logoutError) {
          // ignore logout errors
        }

        removeAllLocalStorage();
        window.location.href = "/auth/login";

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response?.status === 204) {
      return Promise.resolve({ status: 204, data: null });
    }

    handleAxiosError(error?.response?.data?.message);
    return Promise.reject(error);
  },
);

export const axiosPrivate = axiosInstance;
